import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sparkles, Rocket, Hash } from 'lucide-react';
import { RELEASE_NOTES_HISTORY, APP_VERSION, LOCAL_BUILD_ID } from '@/hooks/useVersionCheck';
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";

export default function Changelog() {
  // Initialize sidebar state from localStorage
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarState');
    return saved ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    localStorage.setItem('sidebarState', JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen]);

  return (
    <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        <main className="flex-1 overflow-auto">
          <div className="container max-w-3xl py-8 px-4">
            <div className="flex items-center gap-4 mb-8">
              <SidebarTrigger className="shrink-0 md:hidden" />
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
                  <Rocket className="h-6 w-6 text-primary-foreground" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold">Changelog</h1>
                  <p className="text-muted-foreground">Alle Versionen und Änderungen</p>
                </div>
              </div>
            </div>

            {/* Current Build Info */}
            <Card className="mb-6 border-dashed">
              <CardContent className="py-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Hash className="h-4 w-4" />
                    <span>Aktuelle Version:</span>
                    <Badge variant="outline">v{APP_VERSION}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">
                    Build: {LOCAL_BUILD_ID}
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-6">
              {RELEASE_NOTES_HISTORY.map((release, releaseIndex) => (
                <Card key={release.version} className={releaseIndex === 0 ? 'border-primary/50 shadow-lg' : ''}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <Badge 
                        variant={releaseIndex === 0 ? "default" : "secondary"} 
                        className="text-sm"
                      >
                        v{release.version}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {new Date(release.date).toLocaleDateString('de-DE', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                      {releaseIndex === 0 && (
                        <Badge variant="outline" className="text-xs bg-primary/10">
                          Aktuell
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="text-lg mt-2">{release.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {release.changes.map((change, changeIndex) => (
                        <div 
                          key={changeIndex} 
                          className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted/80 transition-colors"
                        >
                          <Sparkles className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                          <span className="text-sm leading-relaxed">{change}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
